import {noop, objectFromEntries} from '@benchling/js-utils';
import {produce} from 'immer';
import React from 'react';

import {SequencePrime} from 'biojs/BioConstants';
import {DisplayPrimer, PrimerType} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import BulkAssemblyPrimersStructuredTableAdapter, {
  BulkAssemblyPrimersStructuredTableMetadata,
} from 'molbio/bulk-assembly/tables/BulkAssemblyPrimersStructuredTableAdapter';
import {formatValidationMessages} from 'molbio/bulk-assembly/tables/BulkAssemblyTableUtil';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {Cell, TableColumnType} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {DEGREE_SIGN, MELTING_TEMP_ABBREVIATION} from 'util/UnicodeSymbols';

enum ColumnKey {
  FRAGMENT = 'FRAGMENT',
  ORIENTATION = 'ORIENTATION',
  ACTION = 'ACTION',
  PRIMER = 'PRIMER',
  BASES = 'BASES',
  TM_WHOLE = 'TM_WHOLE',
  STATUS = 'STATUS',
}

interface ColumnInfo {
  columnKey: ColumnKey;
  structuredTableColumn: StructuredTableColumn;
}

function getColumns(): Array<ColumnInfo> {
  return [
    {
      columnKey: ColumnKey.FRAGMENT,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Fragment',
        columnType: TableColumnType.STRING,
        isReadOnly: true,
        width: 135,
      }),
    },
    {
      columnKey: ColumnKey.ORIENTATION,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Orientation',
        columnType: TableColumnType.STRING,
        isReadOnly: true,
      }),
    },
    {
      columnKey: ColumnKey.ACTION,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Action',
        columnType: TableColumnType.STRING,
        isReadOnly: true,
        width: 154,
      }),
    },
    {
      columnKey: ColumnKey.PRIMER,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Primer',
        columnType: TableColumnType.BULK_ASSEMBLY_PRIMER,
        isReadOnly: true,
        width: 154,
      }),
    },
    {
      columnKey: ColumnKey.BASES,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Bases',
        columnType: TableColumnType.BULK_ASSEMBLY_PRIMER_BASES,
        isReadOnly: true,
        width: 145,
      }),
    },
    {
      columnKey: ColumnKey.TM_WHOLE,
      structuredTableColumn: makeStructuredTableColumn({
        name: `${MELTING_TEMP_ABBREVIATION} whole (${DEGREE_SIGN}C)`,
        columnType: TableColumnType.STRING,
        isReadOnly: true,
      }),
    },
    {
      columnKey: ColumnKey.STATUS,
      structuredTableColumn: makeStructuredTableColumn({
        name: 'Status',
        columnType: TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS,
        isReadOnly: true,
        width: 300,
      }),
    },
  ];
}

const ORIENTATION_DISPLAY_TEXT = {
  [SequencePrime.FIVE]: "5' primer",
  [SequencePrime.THREE]: "3' primer",
};

const PRIMER_TYPE_DISPLAY_TEXT = {
  [PrimerType.USE_EXISTING]: 'Use existing primer',
  [PrimerType.CREATE_NEW]: 'Design new primer',
};

function preparePrimerCell(primer: DisplayPrimer): Cell {
  if (primer.primerType === PrimerType.CREATE_NEW && primer.createdOligoId) {
    // For finalized assemblies, show a link to the newly-created oligo.
    return {
      text: '',
      typedValue: {
        type: BaseTypedValueType.BIOENTITY,
        bioentitySummary: {id: primer.createdOligoId, name: primer.name ?? ''},
      },
    };
  } else if (primer.primerType === PrimerType.USE_EXISTING && primer.oligoId) {
    return {
      text: '',
      typedValue: {
        type: BaseTypedValueType.BIOENTITY,
        bioentitySummary: {id: primer.oligoId, name: primer.name ?? ''},
      },
    };
  }
  return {text: primer.name ?? ''};
}

function preparePrimerBasesCell(primer: DisplayPrimer): Cell {
  return {
    text: '',
    typedValue: {
      type: BaseTypedValueType.STRING,
      rawValue: primer.bases ?? '',
    },
  };
}

interface BulkAssemblyPrimersTableProps {
  assemblyId: string;
  constructCid: string;
  primers: Array<DisplayPrimer>;
  isReadOnly: boolean;
}

export default function BulkAssemblyPrimersTable({
  assemblyId,
  constructCid,
  primers,
  isReadOnly,
}: BulkAssemblyPrimersTableProps): JSX.Element {
  const tableCid = `${assemblyId}-${constructCid}-primersTable`;
  const adapter = BulkAssemblyPrimersStructuredTableAdapter.getAdapter();

  const columns: Array<ColumnInfo> = getColumns();
  const columnIndexByKey = objectFromEntries(columns.map((column, i) => [column.columnKey, i]));
  const tableMetadata: BulkAssemblyPrimersStructuredTableMetadata = {
    columns: columns.map((column) => column.structuredTableColumn),
  };

  const primersTable = adapter.createTable({
    // "name" is the table subtitle, which we omit. The actual title is defined in the adapter.
    name: '',
    metadata: tableMetadata,
    entryId: null,
    schemas: [],
    selectorsById: {},
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  });
  primersTable.rows = primers.map((primer, rowIndex) => {
    const row = adapter.getBlankRow(tableMetadata, rowIndex, primersTable.columns);
    // TODO: Show the actual oligo chip in the Primer column
    return produce(row, (draft) => {
      draft.cells[columnIndexByKey[ColumnKey.FRAGMENT]] = {text: primer.fragmentName};
      draft.cells[columnIndexByKey[ColumnKey.ORIENTATION]] = {
        text: ORIENTATION_DISPLAY_TEXT[primer.orientation],
      };
      draft.cells[columnIndexByKey[ColumnKey.ACTION]] = {
        text: PRIMER_TYPE_DISPLAY_TEXT[primer.primerType],
      };
      draft.cells[columnIndexByKey[ColumnKey.PRIMER]] = preparePrimerCell(primer);
      draft.cells[columnIndexByKey[ColumnKey.BASES]] = preparePrimerBasesCell(primer);
      draft.cells[columnIndexByKey[ColumnKey.TM_WHOLE]] = {
        text: primer.wholeMeltingTemperature?.toFixed(2).toString() ?? '',
      };
      draft.cells[columnIndexByKey[ColumnKey.STATUS]] = {
        text: formatValidationMessages(primer.validationMessages, isReadOnly),
      };
    });
  });

  return (
    <StructuredTableOutsideNotebook
      structuredTable={{...primersTable, cid: tableCid}}
      cid={tableCid}
      // Primers table is currently read-only
      updateHandler={noop}
      includeUnsubmittedStatus={false}
      hideColumnHeaderIcons={true}
      canRename={false}
    />
  );
}
